import React from 'react';
import PropTypes from 'prop-types';
import { parseISO, format } from 'date-fns';

import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Paper from '@material-ui/core/Paper';

import EventInfo from './EventInfo';

import { formatNumber } from '../../Utilities/Helpers';

import './EpisodeTimeline.scss';

const useStyles = makeStyles(() => ({
  dates: {
    maxWidth: '45px',
    textAlign: 'left',
    padding: '6px 10px'
  }
}));



export default function EpisodeTimeline({
  episodes,
  hazardOption,
  loc,
  setEpisodes,
  setEventNarratives
}) {
  const classes = useStyles();

  let timelineItems = [];
  let countyAndNotCount = loc.id.length === 5 && hazardOption !== 'count';

  // Combines episodes as necessary then adds JSX to timelineItems to represent the new episode
  for (const [episodeID, episodeData] of Object.entries(episodes.episodes)) {
    let events = episodeData['events'];
    
    let eventsHere = events.filter((event) => event.counties.includes(loc.id));
    if (events.length === 0 || (countyAndNotCount && eventsHere.length === 0)) {
      continue;
    }

    if (countyAndNotCount) {
      events = eventsHere;
    }
    
    let startDate = parseISO(episodeData['beginTime']);
    let start = format(startDate, 'MM/dd');
    let end = format(parseISO(episodeData['endTime']), 'MM/dd');
    
    let countyTotal = 0;
    let namesAndAmounts = {};
    let itemList = [];

    events.forEach((event) => {
      let amount = 0;

      if (hazardOption === 'injuries') {
        amount = event['injuryDirect'] + event['injuryIndirect'];
      } else if (hazardOption === 'deaths') {
        amount = event['deathsDirect'] + event['deathsIndirect'];
      } else if (hazardOption === 'count') {
        amount = 0;
      } else {
        amount = event[hazardOption];
      }
      

      let cName;
      if (countyAndNotCount) {
        cName = event.event;
      } else if (typeof event.counties === 'string') {
        cName = loc.id;
      } else {
        cName = event.counties[0] || 'undefined';
      }

      if (Object.keys(namesAndAmounts).includes(cName)) {
        namesAndAmounts[cName] += amount;
      } else {
        namesAndAmounts[cName] = amount;
      }

      countyTotal += amount;
    });

    for (const name in namesAndAmounts) {
      itemList.push(
        <EventInfo
          key={name}
          name={name}
          amount={namesAndAmounts[name]}
          fips={!countyAndNotCount}
          events={events}
          hazardOption={hazardOption}
          setEventNarratives={setEventNarratives}
        />
      );
    }

    if (hazardOption !== 'count' && itemList.length > 1) {
      itemList.push(<Typography><b>Total: {hazardOption.includes('damage') ? '$' : ''}{formatNumber(countyTotal)}</b></Typography>);
    }

    timelineItems.push(
      [startDate, <TimelineItem key={episodeID}>
        <TimelineOppositeContent className={classes.dates}>
          <Typography color="textSecondary">{start}-</Typography>
          <Typography color="textSecondary">{end}</Typography>
        </TimelineOppositeContent>
        <TimelineSeparator>
          <TimelineDot color="secondary" />
          <TimelineConnector />
        </TimelineSeparator>
        <TimelineContent>
          <Paper elevation={2}>
            {(itemList[0].props.children && itemList[0].props.children.includes('was caused')) ? '' : ((hazardOption === 'count') ? <>
              {<Typography><b>Impacted Counties</b></Typography>}
            </> : <>
              {episodes['episodes'][episodeID]['events'].length !== 0 && 
                  <Typography><b>Impact by {loc.id.length === 2 ? 'County' : 'Event ID'}</b></Typography>}
            </>)}
            {itemList}
            <hr/>
            <Typography>{episodes['episodes'][episodeID]['narrative'] || 'No narrative is available for this episode.'}</Typography>
          </Paper>
        </TimelineContent>
      </TimelineItem>]
    );
  }

  timelineItems = timelineItems.sort(function(a,b){
    return a[0] - b[0];
  });

  timelineItems = timelineItems.map((item) => { return item[1]; });

    
  return (
    <div id="episode-timeline-container" className="card">
      <div className="close-btn"><CloseIcon onClick={() => setEpisodes({...episodes, show: false})} /></div>
      <div id="timeline-title">{episodes.year}</div>
      <div id="timeline">
        <Timeline>
          {episodes && timelineItems && (timelineItems.length !== 0 ? timelineItems : <Typography variant="body2" className="no-timeline">
            There were no reported hazard events and/or damages for <b>{episodes.year}</b> in this location.
          </Typography>)}
        </Timeline>
      </div>
    </div>
  );
}


EpisodeTimeline.propTypes = {
  episodes: PropTypes.object,
  hazardOption: PropTypes.string,
  loc: PropTypes.object,
  setEpisodes: PropTypes.func,
  setEventNarratives: PropTypes.func,
};